import { useSession, useTranslation } from '@nextgen-web-framework/all';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import { Logger } from '@nextgen-web-framework/core';
import {
  DynamicObjectInterface,
  RenewalNudgeType,
  updateAccountAutoRenewalData,
  CartAccountProcess,
} from '@commerce-profile-webcomponents/sdk';
import { productApi, ProjectionType } from '@commerce-product-webcomponents/sdk';
import logObject from '../../logObject';
import { parseString, parseNumber, parseArray } from './helper';
import { useCartConfig, useMyAccountProfileConfig } from './masterdata';
import { ErrorConstants, AccountTypeCode, AccountLifeCycleKitType } from './Constants';
import { handleAPIResponseError } from './profileUtils';
import { useLocale } from './customHooks';
import { accountRenewalBannerStatus } from './accountLifeCycleUtils';
import { useGlobalErrorHandler } from './globalErrorHook';

export const useAutoRenewal = () => {
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { isABO, session } = useSession();
  const { id, aboNum, accountExpirationDate, type } = session;
  const profileId = parseString(id);
  const aboNumber = parseString(aboNum);
  const apiErrorHandler = useGlobalErrorHandler();

  const accountAutoRenewalData = useSelector((state: AppState) => state.commerceProfile.accountAutoRenewalData);
  const { selectedFeeSku } = accountAutoRenewalData;

  // Get cart config from master data
  const cartConfigData = useCartConfig();
  const [cartConfig] = cartConfigData;
  const renewalConfig = cartConfig?.accountProcess?.renewal;
  const memberTypeKey = AccountTypeCode[parseString(type)] as keyof CartAccountProcess;
  const skuConfig = renewalConfig?.[memberTypeKey as keyof CartAccountProcess];
  const skuDefaultKey = parseString(skuConfig?.default, AccountLifeCycleKitType.P_KIT);
  const defaultSku = parseString(skuConfig?.[skuDefaultKey]);

  // Configure for starter kits
  const starterKits: DynamicObjectInterface[] = [];
  if (skuConfig?.eKit) {
    starterKits.push({
      sku: parseString(skuConfig?.eKit),
      tncEnabled: true,
      productName: t('profile.AccountRenewal.selectKit.eKit'),
      productDescription: t('profile.AccountRenewal.selectKit.selectedEKitRenewal.desc'),
      isEKit: true,
    });
  }
  starterKits.push({
    sku: parseString(skuConfig?.pKit),
    tncEnabled: false,
    productName: t('profile.AccountRenewal.selectKit.pKit'),
    productDescription: t('profile.AccountRenewal.selectKit.selectedPKit.desc'),
    isEKit: false,
  });

  const initAutoRenewalData = async (regId?: string) => {
    const logger = Logger.getInstance(logObject('accountLifeCycleHook.ts', 'useAutoRenewal'));

    try {
      if (regId || !selectedFeeSku) {
        const productRes = await apiErrorHandler(() =>
          productApi.getProductByIds({
            idList: [parseString(skuConfig?.fee)],
            projections: ProjectionType.Detailed,
          }),
        );
        const [product] = parseArray(productRes?.products);
        const [item] = parseArray(product?.items);
        const [price] = parseArray(item?.prices);
        const regIdObj = regId ? { registrationId: regId } : {};

        dispatch(
          // Set default values
          updateAccountAutoRenewalData({
            ...accountAutoRenewalData,
            ...regIdObj,
            selectedKitSku: '',
            selectedFeeSku: skuConfig?.fee,
            changeKitSku: true,
            checkoutProfile: null,
            currency: parseString(price?.currency),
            totalAmount: parseNumber(price?.priceIncludingTax),
            formattedPrice: parseString(price?.formattedPrice),
            qty: 1,
          }),
        );
      }
      return true;
    } catch (error: DynamicObjectInterface) {
      logger.error('Error initializing auto renewal data (product):', error);
      return false;
    }
  };

  // error handling
  const commonErrorHandling = (error: DynamicObjectInterface, flowName: string, showError?: Function) => {
    // this error for general or comregistrationas-internal-server-error
    const errorObject = {
      headline: 'profile.AccountRenewal.error.UnableToProcess',
      message: 'profile.AccountRenewal.error.invalid-registration', // default error message
    };

    const errorCode = handleAPIResponseError(error);
    if (errorCode !== ErrorConstants.REGISTRATION_INTERNAL_SERVER_ERROR) {
      errorObject.message = `profile.AccountRenewal.error.${flowName}.${errorCode}`;
    }

    showError?.(errorObject);

    return errorObject;
  };

  return {
    t,
    dispatch,

    // Session data
    session,
    isABO,
    profileId,
    aboNum: aboNumber,
    accountExpirationDate: accountExpirationDate ? new Date(accountExpirationDate) : null,

    // Functions/data
    initAutoRenewalData,
    defaultSku,
    starterKits,
    commonErrorHandling,
  };
};

// This function is to export for Cart Team
export const useRenewalStatus = () => {
  const myAccountConfig = useMyAccountProfileConfig();
  const siteLocale = useLocale();
  const { session } = useSession();
  const apiErrorHandler = useGlobalErrorHandler();

  const getRenewalNudgeStatus = async (): Promise<RenewalNudgeType> => {
    const { aboNum } = session;
    if (!aboNum) {
      // for non-login user
      return {
        showRenewalNudge: false,
        isAutoRenewalEnabled: false,
        isAutoRenewalFailed: false,
      };
    }

    const accountRenewalStatus = await accountRenewalBannerStatus(
      session,
      myAccountConfig,
      siteLocale,
      null,
      true,
      apiErrorHandler,
    );
    const { isAutoRenewalEnabled, isAutoRenewalFailed, showRenewalNudge } = accountRenewalStatus || {};

    return {
      showRenewalNudge,
      isAutoRenewalEnabled,
      isAutoRenewalFailed,
    } as RenewalNudgeType;
  };

  return {
    getRenewalNudgeStatus,
  };
};
